// Server-side search.
//
// The catalog is ~41k works and growing toward ~200k; the client holds a page
// of a few hundred. So a typed query CANNOT be answered by filtering what's
// loaded — that only ever searches the current page, which looks like a broken
// search rather than a partial one.
//
// SearchWorks matches against every recorded title variant (romaji, English,
// native script, fan abbreviations), which is why "snk", "shingeki" and
// "attack on titan" all find the same work.

import { Code, ConnectError } from "@connectrpc/connect";

import { searchClient } from "../lib/connect";
import type { Filters, Kind, Work } from "./types";

export interface SearchOutcome {
  works: Work[];
  /** Set when search is unavailable, so the UI can say so rather than showing an empty list. */
  error?: string;
}

export async function searchWorks(
  query: string, kind: Kind, filters: Filters, signal?: AbortSignal,
): Promise<SearchOutcome> {
  try {
    const res = await searchClient.searchWorks({
      query,
      filters: {
        kinds: [kind === "anime" ? 1 : 2],
        yearFrom: filters.yearFrom ?? 0,
        yearTo: filters.yearTo ?? 0,
      },
      pageSize: 60,
    }, { signal });

    return {
      works: res.hits.map((h) => hitToWork(h)),
    };
  } catch (err) {
    if (signal?.aborted) throw err;
    if (err instanceof ConnectError && err.code === Code.Unimplemented) {
      return { works: [], error: "search isn't implemented on this backend yet" };
    }
    return { works: [], error: err instanceof ConnectError ? err.rawMessage : "search failed" };
  }
}

type Hit = Awaited<ReturnType<typeof searchClient.searchWorks>>["hits"][number];

function hitToWork(h: Hit): Work {
  const w = h.work!;
  const kind: Kind = w.kind === 1 ? "anime" : "manga";
  const units = kind === "anime" ? w.episodeCount : w.chapterCount;
  const score = w.stats?.[0]?.score ?? 0;

  return {
    id: w.id,
    kind,
    title: w.displayTitle,
    year: w.startDate?.year ?? 0,
    by: w.organizations?.find((o) => o.isMain)?.name ?? w.organizations?.[0]?.name ?? "Unknown",
    genres: (w.tags ?? []).filter((t) => t.tag?.category === "genre").map((t) => t.tag!.name),
    moods: (w.tags ?? []).filter((t) => t.tag?.category === "mood").map((t) => t.tag!.name),
    // Stored 0-100 upstream-normalized; the UI shows the familiar 0-10.
    score: score / 10,
    pop: 0,
    len: units <= 13 ? "short" : units <= 50 ? "medium" : "long",
    status: w.status === 2 ? "airing" : w.status === 5 ? "hiatus" : "finished",
    count: units ? `${units} ${kind === "anime" ? "eps" : "ch"}` : "—",
    coverPath: w.cover?.proxyPath || undefined,
  };
}
